import { EVENT_IRRIGATION_UNITS, IrrigationEvent } from '@cibo/core'
import { DateRangePickerField, NumberInputField, SelectField } from '@cibo/ui'
import ClearIcon from '@mui/icons-material/Clear'
import EditRounded from '@mui/icons-material/EditRounded'
import {
  Button,
  Collapse,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import { Formik, FormikHelpers, useField } from 'formik'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { EventComponentProps } from '../EventDetail/EventDetailTypes'
import { useIsRequired } from '../useIsRequired'

const IrrigationEventValidationSchema = ({ t }: any) =>
  Yup.object().shape({
    startDate: Yup.date()
      .required(t('required', { context: 'startDate' }))
      .typeError(t('required', { context: 'startDate' })),
    endDate: Yup.date()
      .required(t('required', { context: 'endDate' }))
      .typeError(t('required', { context: 'endDate' })),
    amount: Yup.number()
      .required(t('required', { context: 'amount' }))
      .min(0)
      .typeError(t('required', { context: 'amount' })),
    unit: Yup.string().required(t('required', { context: 'unit' })),
  })

export const IrrigationEventEditorSimple = ({
  editing,
  year,
  name,
  onEditEvent,
  onPressRemove,
  requirement,
  isImmutable,
  eventIndex,
  parentName,
}: EventComponentProps) => {
  const { t } = useTranslation('@cibo/landmanager/EventIrrigationEditor')
  const [{ value }, { initialValue, touched }, { setValue, setTouched }] =
    useField<IrrigationEvent>(name)
  const [localEditing, setLocalEditing] = useState(!initialValue)
  const isRequired = useIsRequired(requirement)

  useEffect(() => {
    onEditEvent(!initialValue)
  }, [!initialValue])

  const startMonth = new Date(year, 4)
  const validationSchema = IrrigationEventValidationSchema({ t })

  const handleSubmit = (
    values: IrrigationEvent,
    { setSubmitting }: FormikHelpers<IrrigationEvent>
  ) => {
    setTouched(true, false)
    setValue(values)
    setLocalEditing(false)
    setSubmitting(false)
    onEditEvent(false)
  }

  const editingDisabled = editing || isImmutable

  return (
    <>
      <Collapse in={!localEditing}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: '275px',
          }}
        >
          <Stack spacing={1}>
            <Typography>
              {value.unit ? t('irrigationType', { context: value.unit }) : '--'}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              divider={<Divider orientation="vertical" flexItem />}
            >
              {value.startDate && value.endDate && (
                <Typography variant="caption">
                  {t('eventDate', {
                    startDate: dayjs(value.startDate),
                    endDate: dayjs(value.endDate),
                  })}
                </Typography>
              )}
              {value.amount !== undefined && (
                <Typography variant="caption">
                  {value.amount} {value.unit && t('irrigationType', { context: value.unit })}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Stack direction="column">
            <Tooltip title={t('edit')}>
              <IconButton
                aria-label={t('editEvent') as string}
                onClick={() => {
                  setLocalEditing(true)
                  onEditEvent(true)
                }}
                size="small"
                disabled={editingDisabled}
              >
                <EditRounded />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('remove')}>
              <IconButton
                aria-label={t('removeEvent') as string}
                onClick={onPressRemove}
                size="small"
                disabled={editingDisabled}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Collapse>
      <Formik
        initialValues={
          value ||
          ({ startDate: null, endDate: null, amount: 0, unit: '' } as unknown as IrrigationEvent)
        }
        validationSchema={validationSchema}
        validateOnMount
        enableReinitialize
        disabled={isImmutable}
        onSubmit={handleSubmit}
      >
        {({ isValid, resetForm, submitForm, values, dirty, errors, getFieldMeta }) => {
          const parentField = getFieldMeta<{ events: IrrigationEvent[] }>(parentName)
          return (
            <Collapse in={localEditing} timeout="auto" unmountOnExit>
              <Stack spacing={2}>
                <FormControl fullWidth required={isRequired('startDate')}>
                  <FormLabel>{t('startDate')}</FormLabel>
                  <DateRangePickerField
                    startRangeFieldName={`startDate`}
                    endRangeFieldName={`endDate`}
                    shouldDisableDate={(day: Date | Dayjs, position) =>
                      parentField.value?.events
                        .filter((f, i) => i !== eventIndex)
                        .some(
                          event =>
                            event.startDate &&
                            event.endDate &&
                            dayjs(day).isSameOrAfter(dayjs(event.startDate)) &&
                            (dayjs(day).isSame(dayjs(event.endDate)) ||
                              dayjs(day).isBefore(dayjs(event.endDate)))
                        ) || false
                    }
                    name={name}
                    minDate={new Date(year - 1, 6, 1)}
                    maxDate={new Date(year + 1, 0, 1)}
                    referenceDate={startMonth}
                  />
                </FormControl>

                <Stack direction="row" spacing={2}>
                  <FormControl sx={{ flex: 1 }} required={isRequired('amount')}>
                    <FormLabel>{t('amount')}</FormLabel>
                    <NumberInputField name={`amount`} data-testid={`amount`} min={0} />
                  </FormControl>

                  <FormControl sx={{ flex: 1 }} required>
                    <FormLabel>{t('unit')}</FormLabel>
                    <SelectField
                      fieldName="unit"
                      options={EVENT_IRRIGATION_UNITS}
                      renderOption={value => t('irrigationType', { context: value })}
                    />
                  </FormControl>
                </Stack>

                <Button
                  variant="outlined"
                  size="small"
                  data-testid={`${name}-cancel`}
                  onClick={() => {
                    !initialValue && !touched && onPressRemove()
                    setLocalEditing(false)
                    onEditEvent(false)
                    resetForm()
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant="contained"
                  data-testid={`${name}-add`}
                  size="small"
                  onClick={() => submitForm()}
                  disabled={!isValid || !dirty}
                >
                  {!initialValue ? t('add') : t('saveEvent')}
                </Button>
              </Stack>
            </Collapse>
          )
        }}
      </Formik>
    </>
  )
}
